/**
 * parseDateQuery — interpret a search query as a date filter, when unambiguous.
 *
 * Returns a matcher against an ISO-ish `created_at` string, evaluated in the
 * browser's local timezone (consistent with formatAbsoluteDate). Returns null
 * when the query is not unambiguously a date — caller should fall through to
 * generic text matching.
 *
 * See docs/dashboard-search-date-parsing.md for the full spec.
 */

const MONTHS: Record<string, number> = {
  jan: 1, january: 1,
  feb: 2, february: 2,
  mar: 3, march: 3,
  apr: 4, april: 4,
  may: 5,
  jun: 6, june: 6,
  jul: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

export type DateQuery = {
  kind: "date";
  match: (createdAt: string | null | undefined) => boolean;
};

function parts(createdAt: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return null;
  return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() };
}

function expandYear(n: number): number {
  if (n >= 100) return n;
  // 2-digit year — assume 2000s
  return 2000 + n;
}

function stripOrdinal(s: string): string {
  return s.replace(/(\d+)(st|nd|rd|th)\b/gi, "$1");
}

export function parseDateQuery(rawInput: string): DateQuery | null {
  const raw = stripOrdinal(rawInput.trim().toLowerCase()).replace(/\s+/g, " ");
  if (!raw) return null;

  // 1) ISO: YYYY-MM-DD
  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return { kind: "date", match: (c) => {
        const p = parts(c); return !!p && p.y === y && p.m === mo && p.d === d;
      }};
    }
  }

  // 2) M/D or M/D/Y (also `-` separator) — must have separator to be a date
  m = raw.match(/^(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?$/);
  if (m) {
    const mo = Number(m[1]), d = Number(m[2]);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      if (m[3]) {
        const y = expandYear(Number(m[3]));
        return { kind: "date", match: (c) => {
          const p = parts(c); return !!p && p.y === y && p.m === mo && p.d === d;
        }};
      }
      return { kind: "date", match: (c) => {
        const p = parts(c); return !!p && p.m === mo && p.d === d;
      }};
    }
  }

  // 3) Month name patterns
  // Tokens: month, optional day, optional year — order: "May", "May 27",
  // "May 2026", "May '26", "May 27 2026", "27 May", "27 May 2026"
  const tokens = raw.split(" ").filter(Boolean);

  const tryMonthToken = (t: string): number | null => {
    const cleaned = t.replace(/[.,]$/, "");
    return MONTHS[cleaned] ?? null;
  };

  // Find a month token
  let monthIdx = -1;
  let monthNum = 0;
  for (let i = 0; i < tokens.length; i++) {
    const mn = tryMonthToken(tokens[i]);
    if (mn) { monthIdx = i; monthNum = mn; break; }
  }

  if (monthIdx >= 0) {
    const others = tokens.filter((_, i) => i !== monthIdx);
    let day: number | null = null;
    let year: number | null = null;
    for (const t of others) {
      // year: 4 digits, or '26 / 26 with leading apostrophe
      const yMatch = t.match(/^'?(\d{2}|\d{4})$/);
      const num = yMatch ? Number(yMatch[1]) : NaN;
      if (yMatch && (t.startsWith("'") || Number(yMatch[1]) >= 32 || yMatch[1].length === 4)) {
        if (year !== null) return null;
        year = expandYear(num);
        continue;
      }
      // day
      const dMatch = t.match(/^(\d{1,2})$/);
      if (dMatch) {
        const dn = Number(dMatch[1]);
        if (dn >= 1 && dn <= 31) {
          if (day !== null) return null;
          day = dn;
          continue;
        }
      }
      // unknown token alongside month — bail
      return null;
    }

    if (day !== null && year !== null) {
      const y = year, mo = monthNum, d = day;
      return { kind: "date", match: (c) => {
        const p = parts(c); return !!p && p.y === y && p.m === mo && p.d === d;
      }};
    }
    if (day !== null) {
      const mo = monthNum, d = day;
      return { kind: "date", match: (c) => {
        const p = parts(c); return !!p && p.m === mo && p.d === d;
      }};
    }
    if (year !== null) {
      const y = year, mo = monthNum;
      return { kind: "date", match: (c) => {
        const p = parts(c); return !!p && p.y === y && p.m === mo;
      }};
    }
    // month only
    const mo = monthNum;
    return { kind: "date", match: (c) => {
      const p = parts(c); return !!p && p.m === mo;
    }};
  }

  // 4) Bare day 1–31 → current month
  const bare = raw.match(/^(\d{1,2})$/);
  if (bare) {
    const dn = Number(bare[1]);
    if (dn >= 1 && dn <= 31) {
      const now = new Date();
      const curMonth = now.getMonth() + 1;
      return { kind: "date", match: (c) => {
        const p = parts(c); return !!p && p.m === curMonth && p.d === dn;
      }};
    }
  }

  // 4-digit year alone, bare numbers >31, anything else → not a date
  return null;
}
